import type { ListBuilderConfiguration } from '../types';

/**
 * Ecommerce Users list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const ECOMMERCE_USER_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-ecommerce-users-all',
			cacheTTL: 600000,
			name: 'All Storefront Users',
			type: 'default',
			active: true,
			defaultSortId: 'firstName-asc',
			filterRules: {
				ecommerceUserStatus: {
					defaultValue: [{ id: 'All', label: 'All' }],
				},
				ecommerceUserActivity: {
					defaultValue: [{ id: 'any', label: 'Any' }],
				},
				ecommerceUserClientSalesRep: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
				},
			},
			columns: [
				{
					id: 'name',
					headers: [
						{
							id: 'name-header',
							text: 'Name',
						},
					],
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'public_user',
							},
							dataConfig: {
								type: 'template',
								template: '{firstName} {lastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'email',
					headers: [
						{
							id: 'email-header',
							text: 'Email',
						},
					],
					fields: [
						{
							id: 'email-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'email',
									objectTable: 'public_user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'public_user',
							fieldName: 'email',
							direction: 'ASC',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					// PD-16471: many storefront users sign in by phone and have no
					// email, so the list shows the phone number too.
					id: 'phoneNumber',
					headers: [
						{
							id: 'phoneNumber-header',
							text: 'Phone',
						},
					],
					fields: [
						{
							id: 'phoneNumber-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'phoneNumber',
									objectTable: 'public_user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'phone',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'public_user',
							fieldName: 'phoneNumber',
							direction: 'ASC',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					// Company the public_user belongs to (fk_company__id, resolved
					// to a name by the service). Keyed on company — NOT the portal
					// site — because a portal_site can be deleted/disabled while the
					// company association is the stable, permanent scope.
					id: 'company',
					headers: [
						{
							id: 'company-header',
							text: 'Company',
						},
					],
					fields: [
						{
							id: 'company-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'companyName',
									objectTable: 'public_user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'roles',
					headers: [
						{
							id: 'roles-header',
							text: 'Roles',
						},
					],
					fields: [
						{
							id: 'roles-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'roleNames',
									objectTable: 'public_user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
								valueMapping: [
									{
										value: 'Ecommerce Customer',
										displayText: 'Buyer',
									},
									{
										value: 'Ecommerce Admin',
										displayText: 'Admin',
									},
								],
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					// PD-17691 — each account as "number · name" over its address,
					// the first one only until "+N more" expands the cell. The cell
					// reads the row's `clients`; its value, and so the CSV export,
					// is `clientSummary`, the same accounts as one line of text.
					id: 'clients',
					headers: [
						{
							id: 'clients-header',
							text: 'Clients',
						},
					],
					fields: [
						{
							id: 'clients-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clientSummary',
									objectTable: 'public_user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'custom',
							customComponent: {
								id: 'ecommerce-user-clients',
							},
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					// PD-17191 — latest storefront activity, or the Online now pill
					// while the user is on a storefront (refreshed every minute by
					// EcommerceUsersList). Sorts on the latest visit, server-side.
					id: 'lastActive',
					headers: [
						{
							id: 'lastActive-header',
							text: 'Last active',
						},
					],
					fields: [
						{
							id: 'lastActive-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'lastActiveDate',
									objectTable: 'public_user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'custom',
							customComponent: {
								id: 'ecommerce-user-last-active',
							},
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'public_user',
							fieldName: 'lastActiveDate',
							direction: 'DESC',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					// PD-17191 — latest successful sign-in, read from the login
					// server for the rows on the page. Not sortable: the sign-in
					// history is not in the tenant database.
					id: 'lastLogin',
					headers: [
						{
							id: 'lastLogin-header',
							text: 'Last login',
						},
					],
					fields: [
						{
							id: 'lastLogin-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'lastLoginDate',
									objectTable: 'public_user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'custom',
							customComponent: {
								id: 'ecommerce-user-last-login',
							},
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [
			'ecommerceUserSearch',
			'ecommerceUserStatus',
			'ecommerceUserActivity',
			'ecommerceUserClientSalesRep',
			'ecommerceUserCompany',
		],
		defaultFilters: {},
	},
	tableSorts: [
		{
			id: 'firstName-asc',
			name: 'First Name (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'firstName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'firstName-desc',
			name: 'First Name (Z-A)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'firstName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'lastName-asc',
			name: 'Last Name (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lastName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'lastName-desc',
			name: 'Last Name (Z-A)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'email-asc',
			name: 'Email (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'email',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'email-desc',
			name: 'Email (Z-A)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'email',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'lastActive-desc',
			name: 'Last Active (Newest)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastActiveDate',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'lastActive-asc',
			name: 'Last Active (Oldest)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lastActiveDate',
					objectTable: 'public_user',
				},
			],
		},
	],
	objects: [
		{
			// PD-17551 — Storefront Users live under Settings › Ecommerce.
			baseUrl: '/settings/storefront-users/:id',
			objectLabel: 'Storefront Users',
			objectTable: 'public_user',
			selectedFields: [
				{
					fieldName: 'id',
					fieldLabel: 'ID',
				},
				{
					fieldName: 'email',
					fieldLabel: 'Email',
				},
				{
					fieldName: 'firstName',
					fieldLabel: 'First Name',
				},
				{
					fieldName: 'lastName',
					fieldLabel: 'Last Name',
				},
				{
					fieldName: 'companyName',
					fieldLabel: 'Company',
				},
				{
					fieldName: 'roleNames',
					fieldLabel: 'Roles',
				},
				{
					fieldName: 'clientSummary',
					fieldLabel: 'Clients',
				},
				{
					// Names only, the Clients column's value before PD-17691.
					fieldName: 'clientNames',
					fieldLabel: 'Client Names',
				},
				{
					fieldName: 'lastActiveDate',
					fieldLabel: 'Last Active',
				},
				{
					fieldName: 'lastLoginDate',
					fieldLabel: 'Last Login',
				},
			],
		},
	],
};
